/**
 * @engine/core - Application Lifecycle
 *
 * Defines the application lifecycle and subsystem management.
 * This provides a structured way to initialize, run, and shutdown
 * applications (editor, player, etc.)
 *
 * Lifecycle stages:
 * 1. Created - Application instantiated
 * 2. Initializing - Subsystems being initialized (async)
 * 3. Ready - All subsystems ready, can start
 * 4. Running - Application is running
 * 5. Paused - Application paused (optional)
 * 6. Shutdown - Application shutting down
 *
 * @example
 * ```typescript
 * const app = new EditorApplication();
 * app.registerSubsystem(new VFSSubsystem());
 * app.registerSubsystem(new ScriptingSubsystem());
 * await app.initialize();
 * app.start();
 * // ... later
 * app.shutdown();
 * ```
 */

// ============================================================================
// TYPES
// ============================================================================

/**
 * Application lifecycle state
 */
export enum AppState {
  Created = "created",
  Initializing = "initializing",
  Ready = "ready",
  Running = "running",
  Paused = "paused",
  ShuttingDown = "shuttingDown",
  Shutdown = "shutdown",
}

/**
 * Subsystem initialization priority
 * Lower values initialize first
 */
export enum SubsystemPriority {
  /** Core systems (VFS, config) - init first */
  Core = 0,
  /** Asset systems (AID, texture loading) */
  Assets = 100,
  /** Runtime systems (scripting, physics) */
  Runtime = 200,
  /** Presentation systems (rendering, audio) */
  Presentation = 300,
  /** Editor-only systems */
  Editor = 400,
  /** User systems - init last */
  User = 1000,
}

/**
 * Subsystem configuration
 */
export interface SubsystemConfig {
  /** Subsystem name (unique identifier) */
  name: string;
  /** Initialization priority (lower = earlier) */
  priority?: SubsystemPriority | number;
  /** Dependencies on other subsystems (by name) */
  dependencies?: string[];
  /** Whether subsystem is optional */
  optional?: boolean;
}

/**
 * Events emitted by Application
 */
export interface AppEvents {
  onStateChange?: (state: AppState, prevState: AppState) => void;
  onSubsystemRegistered?: (subsystem: ISubsystem) => void;
  onSubsystemInitialized?: (subsystem: ISubsystem) => void;
  onSubsystemError?: (subsystem: ISubsystem, error: Error) => void;
  onReady?: () => void;
  onStart?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onShutdown?: () => void;
}

// ============================================================================
// SUBSYSTEM INTERFACE
// ============================================================================

/**
 * Subsystem - A modular part of the application
 *
 * Subsystems handle specific functionality (VFS, scripting, rendering, etc.)
 * and have a well-defined lifecycle integrated with the Application.
 */
export interface ISubsystem {
  /** Subsystem configuration */
  readonly config: SubsystemConfig;

  /**
   * Called when subsystem is registered with application.
   * Use to store reference to app and other setup.
   */
  onRegister?(app: IApplication): void;

  /**
   * Initialize the subsystem (async).
   * Called in priority order after all subsystems are registered.
   * Dependencies are guaranteed to be initialized first.
   */
  onInitialize?(): Promise<void> | void;

  /**
   * Called when ALL subsystems are initialized.
   * Use for cross-subsystem setup that requires all systems ready.
   */
  onReady?(): void;

  /**
   * Called when application starts running.
   */
  onStart?(): void;

  /**
   * Called when application is paused.
   */
  onPause?(): void;

  /**
   * Called when application resumes from pause.
   */
  onResume?(): void;

  /**
   * Called each frame (if subsystem needs per-frame updates).
   * @param deltaTime Time since last frame in seconds
   */
  onUpdate?(deltaTime: number): void;

  /**
   * Called at fixed timestep (for physics, etc).
   * @param fixedDeltaTime Fixed timestep in seconds
   */
  onFixedUpdate?(fixedDeltaTime: number): void;

  /**
   * Shutdown the subsystem.
   * Called in reverse priority order (last registered = first shutdown).
   */
  onShutdown?(): void;
}

/**
 * Application interface (for subsystem reference)
 */
export interface IApplication {
  /** Current application state */
  readonly state: AppState;

  /** Get a registered subsystem by name */
  getSubsystem<T extends ISubsystem>(name: string): T | undefined;

  /** Check if a subsystem is registered */
  hasSubsystem(name: string): boolean;

  /** Report a fatal error from a subsystem (triggers crash handling) */
  reportCrash(subsystem: ISubsystem, error: Error): void;
}

// ============================================================================
// BASE APPLICATION CLASS
// ============================================================================

/**
 * Application - Base class for engine applications
 *
 * Provides:
 * - Subsystem registration and lifecycle management
 * - Dependency-aware initialization order
 * - Pause/resume functionality
 * - Graceful shutdown
 *
 * Extend this for specific applications:
 * - EditorApplication - Game editor with Vue integration
 * - PlayerApplication - Standalone game player
 */
export abstract class Application implements IApplication {
  protected _state: AppState = AppState.Created;
  protected _subsystems: Map<string, ISubsystem> = new Map();
  protected _initOrder: string[] = [];
  protected _events: AppEvents = {};

  /** Application name */
  abstract readonly name: string;

  // ==========================================================================
  // STATE
  // ==========================================================================

  get state(): AppState {
    return this._state;
  }

  get isInitialized(): boolean {
    return (
      this._state === AppState.Ready ||
      this._state === AppState.Running ||
      this._state === AppState.Paused
    );
  }

  get isRunning(): boolean {
    return this._state === AppState.Running;
  }

  get isPaused(): boolean {
    return this._state === AppState.Paused;
  }

  // ==========================================================================
  // SUBSYSTEM MANAGEMENT
  // ==========================================================================

  /**
   * Register a subsystem
   */
  registerSubsystem(subsystem: ISubsystem): this {
    if (this._state !== AppState.Created) {
      throw new Error(
        `Cannot register subsystems after initialization started`,
      );
    }

    const name = subsystem.config.name;
    if (this._subsystems.has(name)) {
      throw new Error(`Subsystem "${name}" already registered`);
    }

    this._subsystems.set(name, subsystem);
    subsystem.onRegister?.(this);
    this._events.onSubsystemRegistered?.(subsystem);

    return this;
  }

  /**
   * Get a registered subsystem
   */
  getSubsystem<T extends ISubsystem>(name: string): T | undefined {
    return this._subsystems.get(name) as T | undefined;
  }

  /**
   * Check if subsystem is registered
   */
  hasSubsystem(name: string): boolean {
    return this._subsystems.has(name);
  }

  /**
   * Get all registered subsystem names
   */
  getSubsystemNames(): string[] {
    return Array.from(this._subsystems.keys());
  }

  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================

  /**
   * Set event handlers
   */
  setEvents(events: AppEvents): this {
    this._events = { ...this._events, ...events };
    return this;
  }

  /**
   * Initialize all subsystems
   */
  async initialize(): Promise<void> {
    if (this._state !== AppState.Created) {
      throw new Error(`Cannot initialize: app is in state "${this._state}"`);
    }

    this.setState(AppState.Initializing);

    // Calculate initialization order based on priority and dependencies
    this._initOrder = this.calculateInitOrder();

    // Initialize subsystems in order
    for (const name of this._initOrder) {
      const subsystem = this._subsystems.get(name)!;

      try {
        await subsystem.onInitialize?.();
        this._events.onSubsystemInitialized?.(subsystem);
      } catch (error) {
        this._events.onSubsystemError?.(subsystem, error as Error);

        if (!subsystem.config.optional) {
          console.error(
            `[${this.name}] Failed to initialize required subsystem "${name}":`,
            error,
          );
          if (error instanceof Error && error.stack) {
            console.error(error.stack);
          }
          throw new Error(
            `Failed to initialize required subsystem "${name}": ${error}`,
          );
        }

        console.warn(
          `[${this.name}] Optional subsystem "${name}" failed:`,
          error,
        );
        if (error instanceof Error && error.stack) {
          console.warn(error.stack);
        }
      }
    }

    // Call onReady on all subsystems
    for (const name of this._initOrder) {
      const subsystem = this._subsystems.get(name)!;
      try {
        subsystem.onReady?.();
      } catch (error) {
        console.error(
          `[${this.name}] Subsystem "${name}" onReady failed:`,
          error,
        );
        if (error instanceof Error && error.stack) {
          console.error(error.stack);
        }
      }
    }

    this.setState(AppState.Ready);
    this._events.onReady?.();
  }

  /**
   * Start the application
   */
  start(): void {
    if (this._state !== AppState.Ready && this._state !== AppState.Paused) {
      throw new Error(`Cannot start: app is in state "${this._state}"`);
    }

    const wasReady = this._state === AppState.Ready;
    this.setState(AppState.Running);

    if (wasReady) {
      // First start
      for (const name of this._initOrder) {
        this._subsystems.get(name)!.onStart?.();
      }
      this._events.onStart?.();
    } else {
      // Resume from pause
      for (const name of this._initOrder) {
        this._subsystems.get(name)!.onResume?.();
      }
      this._events.onResume?.();
    }
  }

  /**
   * Pause the application
   */
  pause(): void {
    if (this._state !== AppState.Running) {
      return;
    }

    this.setState(AppState.Paused);

    for (const name of this._initOrder) {
      this._subsystems.get(name)!.onPause?.();
    }

    this._events.onPause?.();
  }

  /**
   * Resume from pause
   */
  resume(): void {
    if (this._state !== AppState.Paused) {
      return;
    }

    this.start(); // Reuses start logic for resume
  }

  /**
   * Shutdown the application
   */
  async shutdown(): Promise<void> {
    if (
      this._state === AppState.Shutdown ||
      this._state === AppState.ShuttingDown
    ) {
      return;
    }

    this.setState(AppState.ShuttingDown);

    // Shutdown in reverse order
    const reverseOrder = [...this._initOrder].reverse();

    for (const name of reverseOrder) {
      const subsystem = this._subsystems.get(name)!;
      try {
        subsystem.onShutdown?.();
      } catch (error) {
        console.error(
          `[${this.name}] Subsystem "${name}" shutdown failed:`,
          error,
        );
        if (error instanceof Error && error.stack) {
          console.error(error.stack);
        }
      }
    }

    this._subsystems.clear();
    this._initOrder = [];

    this.setState(AppState.Shutdown);
    this._events.onShutdown?.();
  }

  // ==========================================================================
  // FRAME UPDATES
  // ==========================================================================

  /**
   * Call update on all subsystems
   */
  update(deltaTime: number): void {
    if (this._state !== AppState.Running) return;

    for (const name of this._initOrder) {
      this._subsystems.get(name)!.onUpdate?.(deltaTime);
    }
  }

  /**
   * Call fixed update on all subsystems
   */
  fixedUpdate(fixedDeltaTime: number): void {
    if (this._state !== AppState.Running) return;

    for (const name of this._initOrder) {
      this._subsystems.get(name)!.onFixedUpdate?.(fixedDeltaTime);
    }
  }

  /**
   * Report a fatal error from a subsystem.
   * Triggers onSubsystemError event for crash handling.
   */
  reportCrash(subsystem: ISubsystem, error: Error): void {
    this._events.onSubsystemError?.(subsystem, error);
  }

  // ==========================================================================
  // INTERNAL
  // ==========================================================================

  protected setState(newState: AppState): void {
    const prevState = this._state;
    this._state = newState;
    this._events.onStateChange?.(newState, prevState);
  }

  /**
   * Calculate initialization order based on priority and dependencies
   */
  protected calculateInitOrder(): string[] {
    const result: string[] = [];
    const visited = new Set<string>();
    const visiting = new Set<string>();

    // Get subsystems sorted by priority
    const sorted = Array.from(this._subsystems.entries()).sort((a, b) => {
      const pA = a[1].config.priority ?? SubsystemPriority.User;
      const pB = b[1].config.priority ?? SubsystemPriority.User;
      return pA - pB;
    });

    // Topological sort with dependencies
    const visit = (name: string) => {
      if (visited.has(name)) return;
      if (visiting.has(name)) {
        throw new Error(`Circular dependency detected involving "${name}"`);
      }

      const subsystem = this._subsystems.get(name);
      if (!subsystem) {
        throw new Error(`Dependency "${name}" not found`);
      }

      visiting.add(name);

      // Visit dependencies first
      const deps = subsystem.config.dependencies ?? [];
      for (const dep of deps) {
        visit(dep);
      }

      visiting.delete(name);
      visited.add(name);
      result.push(name);
    };

    for (const [name] of sorted) {
      visit(name);
    }

    return result;
  }
}

// ============================================================================
// SUBSYSTEM BASE CLASS
// ============================================================================

/**
 * Base class for subsystems (optional, can just implement ISubsystem)
 */
export abstract class Subsystem implements ISubsystem {
  protected _app: IApplication | null = null;

  // TODO: lazy instance of logger with subsystem name

  abstract readonly config: SubsystemConfig;

  /** Get the application reference */
  protected get app(): IApplication {
    if (!this._app) {
      throw new Error("Subsystem not registered with application");
    }
    return this._app;
  }

  /** Get another subsystem by name */
  protected getSubsystem<T extends ISubsystem>(name: string): T {
    const sub = this.app.getSubsystem<T>(name);
    if (!sub) {
      throw new Error(`Required subsystem "${name}" not found`);
    }
    return sub;
  }

  /**
   * Report a fatal crash from this subsystem.
   * Triggers the application's crash handling (crash screen in production).
   */
  protected crash(message: string, cause?: unknown): void {
    const error = cause instanceof Error
      ? cause
      : new Error(message, cause != null ? { cause } : undefined);
    if (!(cause instanceof Error)) {
      error.message = message;
    }
    this.app.reportCrash(this, error);
  }

  onRegister(app: IApplication): void {
    this._app = app;
  }
}
